import React, { useState, useEffect, useRef } from 'react';
import {
  X,
  Calendar as CalendarIcon,
  Tag,
  Link as LinkIcon,
  FileText,
  Building2,
  Globe,
  Star,
  CheckCircle2,
  AlertCircle,
  Clock,
  MapPin,
  PlusCircle,
} from 'lucide-react';
import { supabase, OperationType, handleSupabaseError } from '../supabase';
import { EventCategory, UserProfile, CryptoEvent, CATEGORY_META } from '../types';
import { format } from 'date-fns';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

interface AddEventModalProps {
  onClose: () => void;
  onSuccess: (message: string) => void;
  isAdmin: boolean;
  userProfile?: UserProfile | null;
  editEvent?: CryptoEvent;
}

const OTHER_CHAINS = ['Ethereum', 'Bitcoin', 'Base', 'Arbitrum', 'Polygon', 'Fogo'];

const FieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="mb-1.5 mt-3 font-bold text-[10px] uppercase tracking-wider text-[#0b0b0c]/55">
    {children}
  </div>
);

const inputBase =
  'w-full h-11 rounded-full border-[1.5px] border-[#0b0b0c] bg-white text-sm font-medium text-[#0b0b0c] placeholder:text-[#0b0b0c]/40 focus:outline-none focus:ring-2 focus:ring-[#9945ff]/30';

export const AddEventModal: React.FC<AddEventModalProps> = ({
  onClose,
  onSuccess,
  isAdmin,
  userProfile,
  editEvent,
}) => {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, []);

  useEffect(() => {
    if (status) {
      const timer = setTimeout(() => setStatus(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [status]);

  const [formData, setFormData] = useState({
    title: editEvent?.title || '',
    description: editEvent?.description || '',
    date: editEvent?.date ? format(editEvent.date, 'yyyy-MM-dd') : '',
    time: editEvent?.time || '',
    allDay: editEvent?.time === 'All day',
    location: editEvent?.location || '',
    category: editEvent?.category || ('conference' as EventCategory),
    chain: (editEvent?.chain as string) || 'Solana',
    link: editEvent?.link || '',
    featured: editEvent?.featured || false,
    companyName: editEvent?.companyName || userProfile?.companyName || '',
  });

  const [chainMode, setChainMode] = useState<'Solana' | 'other'>(
    editEvent?.chain === 'Solana' || !editEvent ? 'Solana' : 'other',
  );
  const [isIndustryWide, setIsIndustryWide] = useState(editEvent?.chain === 'General Crypto');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const { data: { user: authUser } } = await supabase.auth.getUser();
    if (!authUser) return;

    setLoading(true);
    setStatus(null);
    try {
      const finalChain = isIndustryWide ? 'General Crypto' : formData.chain;
      const finalTime = formData.allDay ? 'All day' : formData.time || null;

      // re-check tier from DB (don't trust local state)
      const { data: meRow } = await supabase
        .from('profiles')
        .select('tier')
        .eq('id', authUser.id)
        .maybeSingle();
      const isTrusted = (meRow as { tier?: string } | null)?.tier === 'trusted';

      let successMsg = '';

      if (editEvent) {
        let newStatus = editEvent.status;
        if (isAdmin || isTrusted) {
          newStatus = editEvent.status;
          successMsg = 'Event updated.';
        } else if (editEvent.status === 'approved') {
          newStatus = 'pending';
          successMsg = 'Event updated and resubmitted for approval.';
        } else {
          newStatus = editEvent.status;
          successMsg = 'Event updated.';
        }

        const updatePayload: Record<string, unknown> = {
          title: formData.title,
          description: formData.description || null,
          starts_at: new Date(formData.date).toISOString(),
          time_label: finalTime,
          location: formData.location || null,
          category: formData.category,
          status: newStatus,
          company_name: formData.companyName || null,
          link_url: formData.link || null,
          chain: finalChain,
        };
        if (isAdmin) updatePayload.featured = formData.featured;

        const { error } = await supabase
          .from('events')
          .update(updatePayload as any)
          .eq('id', editEvent.id);
        if (error) throw error;

        setStatus({ type: 'success', message: successMsg });
        setSubmitted(true);
        onSuccess(successMsg);
      } else {
        const eventStatus = isAdmin || isTrusted ? 'approved' : 'pending';

        const insertPayload = {
          title: formData.title,
          description: formData.description || null,
          starts_at: new Date(formData.date).toISOString(),
          time_label: finalTime,
          location: formData.location || null,
          category: formData.category,
          chain: finalChain,
          link_url: formData.link || null,
          company_name: formData.companyName || null,
          status: eventStatus as 'approved' | 'pending',
          featured: isAdmin ? formData.featured : false,
          source: (isAdmin ? 'admin' : 'client') as 'admin' | 'client',
          created_by: authUser.id,
        };

        const { error } = await supabase.from('events').insert(insertPayload as any);
        if (error) throw error;

        if (isAdmin) successMsg = 'Event added successfully.';
        else if (isTrusted) successMsg = "Event added. It's now live on the calendar.";
        else successMsg = "Event submitted for approval. We'll review it shortly.";

        setStatus({ type: 'success', message: successMsg });
        setSubmitted(true);
        setFormData({
          title: '',
          description: '',
          date: '',
          time: '',
          allDay: false,
          location: '',
          category: 'conference' as EventCategory,
          chain: 'Solana',
          link: '',
          featured: false,
          companyName: userProfile?.companyName || '',
        });
        setChainMode('Solana');
        setIsIndustryWide(false);

        onSuccess(successMsg);
      }
    } catch (error) {
      const op = editEvent ? OperationType.UPDATE : OperationType.INSERT;
      setStatus({
        type: 'error',
        message: `Failed to ${editEvent ? 'update' : 'submit'} event. Please try again.`,
      });
      handleSupabaseError(error, op, 'events');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center safe-overlay bg-[#0b0b0c]/40">
      <div className="sticker bg-[#e9dffa] w-full max-w-lg mobile-sheet overflow-hidden flex flex-col">
        <div className="flex items-start justify-between gap-3 p-4 sm:p-6 border-b border-[#0b0b0c]/15">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 sticker-soft bg-[#9945ff] flex items-center justify-center shrink-0">
              <PlusCircle className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="font-bold text-[10px] uppercase tracking-wider text-[#0b0b0c]/55">
                {editEvent ? 'Update' : 'Submit'}
              </p>
              <h2 className="font-bold text-xl sm:text-2xl uppercase tracking-tight text-[#0b0b0c]">
                {editEvent ? 'Edit event' : 'Add new event'}
              </h2>
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="w-11 h-11 sm:w-9 sm:h-9 rounded-full border-[1.5px] border-[#0b0b0c] bg-white hover:bg-[#0b0b0c] hover:text-white flex items-center justify-center transition-colors shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div ref={scrollRef} className="overflow-y-auto custom-scrollbar flex-1 p-4 sm:p-6">
          {submitted ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <div className="w-16 h-16 sticker-soft bg-[#14f195] flex items-center justify-center mb-5">
                <CheckCircle2 className="w-8 h-8 text-[#0b0b0c]" />
              </div>
              <h3 className="font-bold text-2xl uppercase tracking-tight text-[#0b0b0c] mb-2">
                {editEvent ? 'Update successful' : 'Submission successful'}
              </h3>
              <p className="text-sm text-[#0b0b0c]/65 mb-6 max-w-sm leading-relaxed">{status?.message}</p>

              <div className="flex flex-col w-full gap-3">
                <button
                  onClick={onClose}
                  className="w-full h-12 rounded-full bg-[#9945ff] text-white border-[1.5px] border-[#0b0b0c] font-bold text-sm uppercase tracking-wider hover:bg-[#5b29a6] transition-colors"
                >
                  {editEvent ? 'Back' : 'Back to calendar'}
                </button>
                {!editEvent && (
                  <button
                    onClick={() => {
                      setSubmitted(false);
                      setStatus(null);
                    }}
                    className="w-full h-12 rounded-full bg-white text-[#0b0b0c] border-[1.5px] border-[#0b0b0c] font-bold text-sm uppercase tracking-wider hover:bg-[#0b0b0c] hover:text-white transition-colors"
                  >
                    Submit another event
                  </button>
                )}
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-1">
              <FieldLabel>Event title</FieldLabel>
              <div className="relative">
                <FileText className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#0b0b0c]/45" />
                <input
                  required
                  type="text"
                  placeholder="e.g. Solana Hacker House Lisbon"
                  className={cn(inputBase, 'pl-10 pr-4')}
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <FieldLabel>Date</FieldLabel>
                  <div className="relative">
                    <CalendarIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#0b0b0c]/45" />
                    <input
                      required
                      type="date"
                      className={cn(inputBase, 'pl-10 pr-4')}
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    />
                  </div>
                </div>
                <div>
                  <FieldLabel>Category</FieldLabel>
                  <div className="relative">
                    <Tag className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#0b0b0c]/45" />
                    <select
                      className={cn(inputBase, 'pl-10 pr-4 appearance-none')}
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value as EventCategory })}
                    >
                      {CATEGORY_META.map((c) => (
                        <option key={c.value} value={c.value}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <FieldLabel>Time</FieldLabel>
                  <div className="relative">
                    <Clock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#0b0b0c]/45" />
                    <input
                      type="time"
                      disabled={formData.allDay}
                      className={cn(inputBase, 'pl-10 pr-4', formData.allDay && 'opacity-40 pointer-events-none')}
                      value={formData.time === 'All day' ? '' : formData.time}
                      onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                    />
                  </div>
                </div>
                <div>
                  <FieldLabel>All day</FieldLabel>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, allDay: !formData.allDay, time: !formData.allDay ? 'All day' : '' })}
                    className={cn(
                      'w-full h-11 rounded-full border-[1.5px] border-[#0b0b0c] text-sm font-bold uppercase tracking-wider transition-colors',
                      formData.allDay ? 'bg-[#0b0b0c] text-white' : 'bg-white text-[#0b0b0c] hover:bg-[#0b0b0c] hover:text-white',
                    )}
                  >
                    {formData.allDay ? 'All day' : 'Specific time'}
                  </button>
                </div>
              </div>

              <FieldLabel>Location</FieldLabel>
              <div className="relative">
                <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#0b0b0c]/45" />
                <input
                  type="text"
                  placeholder="e.g. Lisbon, Portugal · or virtual link"
                  className={cn(inputBase, 'pl-10 pr-4')}
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                />
              </div>

              <FieldLabel>Chain / network</FieldLabel>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {(['Solana', 'other'] as const).map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    disabled={isIndustryWide}
                    onClick={() => {
                      setChainMode(mode);
                      if (mode === 'Solana') setFormData({ ...formData, chain: 'Solana' });
                      else if (!OTHER_CHAINS.includes(formData.chain))
                        setFormData({ ...formData, chain: OTHER_CHAINS[0] });
                    }}
                    className={cn(
                      'h-11 sm:h-10 rounded-full text-xs font-bold uppercase tracking-wider border-[1.5px] border-[#0b0b0c] transition-colors',
                      chainMode === mode
                        ? 'bg-[#0b0b0c] text-white'
                        : 'bg-white text-[#0b0b0c] hover:bg-[#0b0b0c] hover:text-white',
                      isIndustryWide && 'opacity-40 pointer-events-none',
                    )}
                  >
                    {mode === 'Solana' ? 'Solana' : 'Other chains'}
                  </button>
                ))}
              </div>

              {chainMode === 'other' && !isIndustryWide && (
                <div className="relative mt-2">
                  <Globe className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#0b0b0c]/45" />
                  <select
                    className={cn(inputBase, 'pl-10 pr-4 appearance-none')}
                    value={formData.chain}
                    onChange={(e) => setFormData({ ...formData, chain: e.target.value })}
                  >
                    {!OTHER_CHAINS.includes(formData.chain) && (
                      <option value={formData.chain}>{formData.chain}</option>
                    )}
                    {OTHER_CHAINS.map((chain) => (
                      <option key={chain} value={chain}>
                        {chain}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="sticker-soft bg-white mt-3 p-3 flex items-start gap-3">
                <input
                  type="checkbox"
                  id="industryWide"
                  className="mt-0.5 w-4 h-4 accent-[#9945ff]"
                  checked={isIndustryWide}
                  onChange={(e) => setIsIndustryWide(e.target.checked)}
                />
                <label htmlFor="industryWide" className="cursor-pointer">
                  <p className="text-sm font-bold text-[#0b0b0c]">Multi-chain / industry-wide</p>
                  <p className="text-[11px] text-[#0b0b0c]/55 mt-0.5">
                    Tick for industry events that aren't tied to a specific blockchain.
                  </p>
                </label>
              </div>

              <FieldLabel>Company name</FieldLabel>
              <div className="relative">
                <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#0b0b0c]/45" />
                <input
                  type="text"
                  placeholder="Your project / company"
                  className={cn(inputBase, 'pl-10 pr-4')}
                  value={formData.companyName}
                  onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                  disabled={!!userProfile?.companyName && !isAdmin}
                />
              </div>

              <FieldLabel>Link (optional)</FieldLabel>
              <div className="relative">
                <LinkIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#0b0b0c]/45" />
                <input
                  type="url"
                  placeholder="https://..."
                  className={cn(inputBase, 'pl-10 pr-4')}
                  value={formData.link}
                  onChange={(e) => setFormData({ ...formData, link: e.target.value })}
                />
              </div>

              <FieldLabel>Description</FieldLabel>
              <textarea
                rows={3}
                placeholder="Tell us more about the event..."
                className="w-full px-4 py-3 rounded-2xl border-[1.5px] border-[#0b0b0c] bg-white text-sm font-medium text-[#0b0b0c] placeholder:text-[#0b0b0c]/40 focus:outline-none focus:ring-2 focus:ring-[#9945ff]/30 resize-none"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />

              {isAdmin && (
                <div className="sticker-soft bg-white mt-3 p-3 flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="featured"
                    className="w-4 h-4 accent-[#ffd24a]"
                    checked={formData.featured}
                    onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                  />
                  <label htmlFor="featured" className="flex items-center gap-2 text-sm font-bold text-[#0b0b0c] cursor-pointer">
                    <Star
                      className={cn(
                        'w-4 h-4',
                        formData.featured ? 'fill-[#ffd24a] text-[#0b0b0c]' : 'text-[#0b0b0c]/35',
                      )}
                    />
                    Featured event
                  </label>
                </div>
              )}

              {status && status.type === 'error' && (
                <div className="sticker-soft bg-[#ff6a6a] text-white mt-3 p-3 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <p className="text-sm font-medium">{status.message}</p>
                </div>
              )}

              <button
                disabled={loading}
                type="submit"
                className="w-full h-12 mt-5 rounded-full bg-[#9945ff] text-white border-[1.5px] border-[#0b0b0c] font-bold text-sm uppercase tracking-wider hover:bg-[#5b29a6] transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
              >
                {loading ? (
                  <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4" />
                    {editEvent
                      ? 'Update event'
                      : isAdmin
                      ? 'Create event'
                      : userProfile?.tier === 'trusted'
                      ? 'Add event'
                      : 'Submit for approval'}
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};
